import { combineSlices, createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { Comment, CommentRelationType, NewComment } from '../../models/MeetingSupport'
import { type ResultExplainerListData } from '../../apps/professional-app/fa/MeetingSummary/ResultExplainer/models'
import { RootState } from '../store'

interface MeetingNoteState {
  isLoading: boolean
  isUpdateLoading: boolean
  meetingNoteId: string | null
  meetingNote: string
  changedExternally: boolean
}

interface CommentsState {
  isLoading: boolean
  isUpdateLoading: boolean
  comments: EntityState<Comment, string>
  commentedAiResultId: string | null
  newComment: NewComment | null
  savedComment: Comment | null
}

interface ResultExplainerState {
  isLoading: boolean
  aiFeatureId: string | null
  result: string | null
  resultContext: string | null
  resultListData: ResultExplainerListData | null
}

const meetingNoteInitialState: MeetingNoteState = {
  isLoading: true,
  isUpdateLoading: false,
  meetingNoteId: null,
  meetingNote: '',
  changedExternally: false,
}

const commentsAdapter = createEntityAdapter<Comment>()

const commentsInitialState: CommentsState = {
  isLoading: true,
  isUpdateLoading: false,
  comments: commentsAdapter.getInitialState(),
  commentedAiResultId: null,
  newComment: null,
  savedComment: null,
}

const resultExplainerInitialState: ResultExplainerState = {
  isLoading: false,
  aiFeatureId: null,
  result: null,
  resultContext: null,
  resultListData: null,
}

const meetingNoteSlice = createSlice({
  name: 'meetingNote',
  initialState: meetingNoteInitialState,
  reducers: {
    setMeetingNoteLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setMeetingNoteUpdateLoading: (state, action: PayloadAction<boolean>) => {
      state.isUpdateLoading = action.payload
    },
    setMeetingNoteId: (state, action: PayloadAction<string>) => {
      state.meetingNoteId = action.payload
    },
    setMeetingNote: (state, action: PayloadAction<string>) => {
      state.meetingNote = action.payload
    },
    setMeetingNoteChangedExternally: (state, action: PayloadAction<boolean>) => {
      state.changedExternally = action.payload
    },
    resetMeetingNotes: () => meetingNoteInitialState,
  },
})

const commentsSlice = createSlice({
  name: 'comments',
  initialState: commentsInitialState,
  reducers: {
    setCommentsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setCommentUpdateLoading: (state, action: PayloadAction<boolean>) => {
      state.isUpdateLoading = action.payload
    },
    setComments: (state, action: PayloadAction<Comment[]>) => {
      commentsAdapter.setAll(state.comments, action.payload)
    },
    addComment: (state, action: PayloadAction<Comment>) => {
      commentsAdapter.addOne(state.comments, action.payload)
    },
    setComment: (state, action: PayloadAction<Comment>) => {
      commentsAdapter.setOne(state.comments, action.payload)
    },
    setNewComment: (state, action: PayloadAction<NewComment>) => {
      state.newComment = action.payload
    },
    clearNewComment: (state) => {
      state.newComment = null
    },
    setSavedComment: (state, action: PayloadAction<Comment>) => {
      state.savedComment = action.payload
    },
    clearSavedComment: (state) => {
      state.savedComment = null
    },
    setCommentedAiResultId: (state, action: PayloadAction<string | null>) => {
      state.commentedAiResultId = action.payload
    },
    resetComments: () => commentsInitialState,
  },
})

const resultExplainerSlice = createSlice({
  name: 'resultExplainer',
  initialState: resultExplainerInitialState,
  reducers: {
    setResultExplainerLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setResultExplainerAiFeatureId: (state, action: PayloadAction<string>) => {
      state.aiFeatureId = action.payload
    },
    setResultExplainerResult: (state, action: PayloadAction<string>) => {
      state.result = action.payload
    },
    setResultExplainerResultContext: (state, action: PayloadAction<string>) => {
      state.resultContext = action.payload
    },
    setResultExplainerResultListData: (state, action: PayloadAction<ResultExplainerListData | null>) => {
      state.resultListData = action.payload
    },
    resetResultExplainer: () => resultExplainerInitialState,
  },
})

export const { setMeetingNoteLoading, setMeetingNoteUpdateLoading, setMeetingNoteId, setMeetingNote, setMeetingNoteChangedExternally, resetMeetingNotes } =
  meetingNoteSlice.actions
export const {
  setCommentsLoading,
  setCommentUpdateLoading,
  setComments,
  addComment,
  setComment,
  setNewComment,
  clearNewComment,
  setSavedComment,
  clearSavedComment,
  setCommentedAiResultId,
  resetComments,
} = commentsSlice.actions
export const {
  setResultExplainerLoading,
  setResultExplainerResultContext,
  setResultExplainerAiFeatureId,
  setResultExplainerResult,
  setResultExplainerResultListData,
  resetResultExplainer,
} = resultExplainerSlice.actions

export const meetingSupportSlice = combineSlices(meetingNoteSlice, commentsSlice, resultExplainerSlice)

const commentsSelectors = commentsAdapter.getSelectors((state: RootState) => state.meetingSummary.meetingSupport.comments.comments)

export const selectMeetingNoteIsLoading = (state: RootState) => state.meetingSummary.meetingSupport.meetingNote.isLoading

export const selectMeetingNoteUpdateIsLoading = (state: RootState) => state.meetingSummary.meetingSupport.meetingNote.isUpdateLoading

export const selectMeetingNoteChangedExternally = (state: RootState) => state.meetingSummary.meetingSupport.meetingNote.changedExternally

export const selectMeetingNoteId = (state: RootState) => state.meetingSummary.meetingSupport.meetingNote.meetingNoteId

export const selectMeetingNote = (state: RootState) => state.meetingSummary.meetingSupport.meetingNote.meetingNote

export const selectFilteredComments = createSelector([commentsSelectors.selectAll], (comments: Comment[]) => {
  return filterDeletedComments(comments)
})

const filterDeletedComments = (comments: Comment[]) => {
  return comments.filter((comment) => {
    if (!comment.metadata.deleted || comment.relationType === CommentRelationType.COMMENT) {
      return true
    }
    const replies = comments.filter((c) => c.relationId === comment.id && c.relationType === CommentRelationType.COMMENT)
    return !replies.every((reply) => reply.metadata.deleted)
  })
}

export const selectCommentsAreLoading = (state: RootState) => state.meetingSummary.meetingSupport.comments.isLoading

export const selectNewComment = (state: RootState) => state.meetingSummary.meetingSupport.comments.newComment

export const selectSavedComment = (state: RootState) => state.meetingSummary.meetingSupport.comments.savedComment

export const selectCommentUpdateIsLoading = (state: RootState) => state.meetingSummary.meetingSupport.comments.isUpdateLoading

export const selectCommentedAiResultId = (state: RootState) => state.meetingSummary.meetingSupport.comments.commentedAiResultId

export const selectResultExplainerIsLoading = (state: RootState) => state.meetingSummary.meetingSupport.resultExplainer.isLoading

export const selectResultExplainerAiFeatureId = (state: RootState) => state.meetingSummary.meetingSupport.resultExplainer.aiFeatureId

export const selectResultExplainerResult = (state: RootState) => state.meetingSummary.meetingSupport.resultExplainer.result

export const selectResultExplainerResultContext = (state: RootState) => state.meetingSummary.meetingSupport.resultExplainer.resultContext

export const selectResultExplainerResultListData = (state: RootState) => state.meetingSummary.meetingSupport.resultExplainer.resultListData
